import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Markdown } from "@/components/site/Markdown";
import { Reveal } from "@/components/site/Reveal";
import { ProductCard } from "@/components/site/ProductCard";
import { NAV } from "@/components/site/Header";
import { Aside } from "./ContentPage";
import NotFound from "./NotFound";
import { PageNotice } from "@/components/site/PageNotice";

function useBrands() {
  const { data: site } = useFetch("/site");
  const hidden = site?.hidden_pages || [];
  return BRANDS.filter((b) => !hidden.includes(`tools-${b.to.split("/").pop()}`));
}

const BRANDS = NAV.find((n) => n.children).children;

export function ToolsIndex() {
  const { data: page } = useFetch("/pages/tuning-tools");
  const brands = useBrands();
  return (
    <div data-testid="tools-page">
      <Seo path="/tuning-tools" />
      <PageHero page={page} crumbs={[["/", "Home"], [null, "Tuning Tools"]]} />
      <section className="container-x py-20">
        <div className="grid gap-px bg-[color:var(--line)] md:grid-cols-2 lg:grid-cols-3">
          {brands.map((b, i) => (
            <Reveal key={b.to} delay={i * 70}>
              <Link to={b.to} className="group flex h-full flex-col justify-between bg-[color:var(--bg)] p-8 transition-colors hover:bg-[color:var(--bg-2)]" data-testid={`tools-brand-${b.to.split("/").pop()}`}>
                <span className="font-mono text-xs text-[color:var(--bronze)]">0{i + 1}</span>
                <div className="mt-14">
                  <h2 className="font-display text-3xl font-bold uppercase text-white">{b.label}</h2>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{b.note}</p>
                </div>
                <ArrowRight className="mt-8 h-5 w-5 text-slate-500 transition-all group-hover:translate-x-1 group-hover:text-[color:var(--ice)]" />
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-16 max-w-3xl"><Markdown testId="page-body">{page?.body}</Markdown></div>
      </section>
    </div>
  );
}

export function BrandPage() {
  const { brand } = useParams();
  const { data: page, error } = useFetch(`/pages/tools-${brand}`);
  const { data: products } = useFetch("/products", { category: brand });
  if (error) return <NotFound />;
  return (
    <div data-testid={`brand-page-${brand}`}>
      <Seo path={`/tuning-tools/${brand}`} />
      <PageNotice page={page} />
      <PageHero page={page} crumbs={[["/", "Home"], ["/tuning-tools", "Tuning Tools"], [null, page?.title]]} />
      <section className="container-x py-16">
        {products?.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="brand-products">
            {products.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 80}><ProductCard p={p} /></Reveal>)}
          </div>
        ) : products && (
          <div className="panel chamfer relative p-8 sm:p-12" data-testid="brand-empty">
            <div className="knot-bg pointer-events-none absolute inset-0 opacity-[0.03]" />
            <h2 className="relative font-display text-3xl font-bold uppercase text-white sm:text-4xl" data-testid="brand-empty-title">{page?.empty_title || "Available to order"}</h2>
            <p className="relative mt-4 max-w-2xl text-slate-300">{page?.empty_text || "Contact us for current availability and pricing."}</p>
            <div className="relative mt-8 flex flex-wrap gap-3"><Link to="/contact" className="btn-ice" data-testid="brand-empty-contact">Contact Jorvik</Link></div>
          </div>
        )}
      </section>
      <section className="container-x grid gap-16 pb-8 lg:grid-cols-[1fr_340px]">
        <Markdown testId="page-body">{page?.body}</Markdown>
        <Aside />
      </section>
    </div>
  );
}
